import {
  canonicalLabel,
  COLLECTIONPOINT_IMPORT_FIELDS,
  ImportFieldDef,
} from './import-schema';

/** Αντιστοιχισμένο πεδίο μετά την ανάγνωση επικεφαλίδας */
export type MappedImportField = {
  def: ImportFieldDef;
  colIdx: number;
  matchedLabel?: string;
  colValue?: unknown;
  fieldValue?: unknown;
};

export type ImportFieldMapState = {
  fields: Record<string, MappedImportField>;
};

/** Κανονικοποίηση κειμένου για σύγκριση επικεφαλίδων / lookups */
export function normalizeHeaderLabel(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Μετατροπή τιμής κελιού Excel σε απλό κείμενο */
export function cellToString(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value).trim();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (v.result != null) return cellToString(v.result);
    if (Array.isArray((v as { richText?: { text?: string }[] }).richText)) {
      const text = ((v as { richText: { text?: string }[] }).richText ?? [])
        .map((p) => p?.text ?? '')
        .join('')
        .trim();
      return text || undefined;
    }
    if (v.text != null) return cellToString(v.text);
  }
  const s = String(value).trim();
  return s === '' ? undefined : s;
}

/** Βρίσκει δείκτη στήλης που ταιριάζει σε κάποιο alias */
function findColumnIndex(headerRow: unknown[], labels: string[]): { idx: number; label?: string } {
  const normalizedHeaders = headerRow.map(normalizeHeaderLabel);
  for (const label of labels) {
    const normalizedLabel = normalizeHeaderLabel(label);
    const idx = normalizedHeaders.findIndex((h) => h === normalizedLabel);
    if (idx >= 0) return { idx, label };
  }
  return { idx: -1 };
}

/**
 * Αντιστοιχίζει επικεφαλίδες Excel στα πεδία schema.
 * Πετάει Error αν λείπουν υποχρεωτικές στήλες.
 */
export function mapHeaderToFields(headerRow: unknown[]): ImportFieldMapState {
  const fields: Record<string, MappedImportField> = {};

  for (const def of COLLECTIONPOINT_IMPORT_FIELDS) {
    const { idx, label } = findColumnIndex(headerRow, def.labels);
    fields[def.field] = { def, colIdx: idx, matchedLabel: label };
  }

  const missingRequired = COLLECTIONPOINT_IMPORT_FIELDS.filter(
    (d) => d.required && fields[d.field].colIdx < 0,
  ).map((d) => canonicalLabel(d));

  if (missingRequired.length) {
    throw new Error(`Λείπουν οι υποχρεωτικές στήλες: ${missingRequired.join(', ')}`);
  }

  return { fields };
}

/** Μηδενίζει τιμές γραμμής πριν την επεξεργασία νέας γραμμής */
export function resetRowFieldValues(state: ImportFieldMapState): void {
  for (const mapped of Object.values(state.fields)) {
    mapped.colValue = undefined;
    mapped.fieldValue = undefined;
  }
}

/** Επωνυμία γραμμής — κενή γραμμή αν λείπει */
export function resolveCollectionpointName(
  state: ImportFieldMapState,
  row: unknown[],
): string | null {
  const mapped = state.fields.name;
  if (!mapped || mapped.colIdx < 0) return null;
  const value = cellToString(row[mapped.colIdx]);
  return value ?? null;
}

/** Ανακτά resolved τιμή πεδίου μετά την επεξεργασία γραμμής */
export function getMappedFieldValue(state: ImportFieldMapState, field: string): unknown {
  return state.fields[field]?.fieldValue;
}

/** Πεδία δεδομένων που επεξεργάζονται ανά γραμμή (με υπάρχουσα στήλη) */
export function dataFieldsForProcessing(state: ImportFieldMapState): MappedImportField[] {
  return Object.values(state.fields).filter((m) => m.colIdx >= 0);
}
